import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import { api } from '../../api/nocode';
import { sendEmail, getLastEmailError } from '../../api/ses';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { Link } from 'react-router-dom';

const PROMPTS = [
  { day: 1, date: '2026-07-13', title: 'MONDAY, JULY 13', prompt: "What do you need to hear about your calling before Day 1 begins? Write to yourself as your most honest, encouraging self. What would you say to a dear friend who is about to do something brave?" },
  { day: 2, date: '2026-07-14', title: 'TUESDAY, JULY 14', prompt: "Yesterday you named your calling. Today you face the manuscript. What truth about your writing life do you need someone to speak over you? What would your wisest, most patient self say?" },
  { day: 3, date: '2026-07-15', title: 'WEDNESDAY, JULY 15', prompt: "Publishing feels like a wall to most authors. What do you need to hear from yourself to take it one faithful step at a time? Remind yourself why the work of getting this book out into the world matters." },
  { day: 4, date: '2026-07-16', title: 'THURSDAY, JULY 16', prompt: "Marketing feels uncomfortable — especially for a Christian author. Remind yourself why your book matters and who specifically needs it. Write to the version of yourself who is tempted to hide." },
  { day: 5, date: '2026-07-17', title: 'FRIDAY, JULY 17', prompt: "You made it through the academy. You have your Compass. What do you want to say to yourself now that you know where you're going? What does the journey ahead look like, and what do you want to remember from this week?" }
];

const quillModules = {
  toolbar: [
    ['bold', 'italic'],
    [{ 'list': 'bullet' }]
  ]
};

const LetterCard = ({ dayData, letter, onSave, onSchedule, onUnschedule, user, devMode, globalCutoff }) => {
  const [content, setContent] = useState(letter?.letter_body || '');
  const [confirming, setConfirming] = useState(false);
  const [autoSaveTime, setAutoSaveTime] = useState(null);
  const [editingUnschedule, setEditingUnschedule] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  
  // Test Controls State
  const [customTime, setCustomTime] = useState(letter?.test_scheduled_time || '');
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [errorToast, setErrorToast] = useState('');
  
  const lastSavedContent = useRef(letter?.letter_body || '');

  const status = letter?.status || 'not_written';
  
  const cutoff = globalCutoff || (() => {
    const c = new Date(dayData.date);
    c.setDate(c.getDate() - 1);
    c.setHours(23, 59, 59, 999);
    return c;
  })();
  
  const isLocked = (new Date() > cutoff) && !devMode && user?.early_access != 1 && user?.role !== 'admin';

  useEffect(() => {
    if (letter?.letter_body !== undefined && letter?.letter_body !== null) {
      setContent(letter.letter_body);
      lastSavedContent.current = letter.letter_body;
    } else if (!letter?.letter_body && content === '') {
      const greeting = `<p>Dear ${user?.name?.split(' ')[0] || 'Friend'},</p><p><br></p>`;
      setContent(greeting);
      lastSavedContent.current = greeting;
    }
  }, [user, letter]);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (content !== lastSavedContent.current && (status === 'not_written' || status === 'draft')) {
        const success = await onSave(dayData.day, content, true);
        if (success) {
          lastSavedContent.current = content;
          const now = new Date();
          setAutoSaveTime(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
          setTimeout(() => setAutoSaveTime(null), 3000);
        } else {
          setErrorToast('Auto-save failed. Check connection.');
          setTimeout(() => setErrorToast(''), 3000);
        }
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [content, status, dayData.day, onSave]);

  const handleManualSave = async () => {
    const success = await onSave(dayData.day, content, false);
    if (success) {
      lastSavedContent.current = content;
      const now = new Date();
      setAutoSaveTime(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      setTimeout(() => setAutoSaveTime(null), 3000);
    } else {
      setErrorToast('Manual save failed. Please try again.');
      setTimeout(() => setErrorToast(''), 3000);
    }
  };

  const getQuoteForDay = (day) => {
    const quotes = [
      "\"The scariest moment is always just before you start.\" — Stephen King",
      "\"You can always edit a bad page. You can't edit a blank page.\" — Jodi Picoult",
      "\"I write because I don't know what I think until I read what I say.\" — Flannery O'Connor",
      "\"A book, too, can be a star, a living fire to lighten the darkness.\" — Madeleine L'Engle",
      "\"A word after a word after a word is power.\" — Margaret Atwood"
    ];
    return quotes[day - 1] || quotes[0];
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      alert("Please enter a valid test email address.");
      return;
    }
    
    setSendingTest(true);
    const subject = `Your Day ${dayData.day} Academy Letter`;
    
    const emailFormattedContent = content
      .replace(/<p>/g, '<p style="margin-top: 0; margin-bottom: 20px;">')
      .replace(/<ul>/g, '<ul style="margin-top: 0; margin-bottom: 20px; padding-left: 20px;">')
      .replace(/<li>/g, '<li style="margin-bottom: 8px;">');

    const htmlBody = `
      <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #fdfaf6; padding: 0;">
        <div style="text-align: center; padding: 40px 20px 20px;">
          <img src="https://www.crossandpen.com/uploads/images/1789473595942-564555159.png" alt="GospelWise Author Academy" style="max-width: 220px; height: auto;" />
        </div>
        <div style="padding: 0 40px 20px;">
          <h2 style="color: #2D5016; margin-bottom: 5px; font-size: 24px; text-align: center;">Day ${dayData.day} Letter</h2>
          <p style="text-align: center; color: #8b7355; font-size: 14px; font-style: italic; margin-top: 0; margin-bottom: 30px;">
            A reminder from your past self
          </p>
          
          <div style="font-size: 17px; line-height: 1.8; background: #ffffff; padding: 40px; border-top: 4px solid #D4A373; border-radius: 8px; color: #2c3e20; box-shadow: 0 4px 15px rgba(0,0,0,0.03);">
            ${emailFormattedContent}
          </div>
          
          <div style="margin-top: 40px; background-color: #EAF1E6; border-left: 4px solid #2D5016; padding: 20px 25px; border-radius: 0 8px 8px 0;">
            <p style="margin: 0; color: #2D5016; font-style: italic; font-size: 15px; line-height: 1.6;">
              ${getQuoteForDay(dayData.day)}
            </p>
          </div>
          
          <p style="margin-top: 40px; text-align: center; font-size: 12px; color: #a39b90;">
            © 2026 Gospel-Wise Author. All rights reserved.<br/>
            This is a test delivery from the Admin Dev Panel.
          </p>
        </div>
      </div>
    `;

    const success = await sendEmail(testEmail, subject, htmlBody);
    setSendingTest(false);

    if (success) {
      alert(`Test email successfully delivered to ${testEmail} via AWS SES!`);
    } else {
      const err = getLastEmailError();
      alert(`Failed to send test email: ${err || 'Please check console for details.'}`);
    }
  };

  const handleCustomSchedule = () => {
    if (!customTime) return alert("Please select a date and time.");
    onSchedule(dayData.day, content, customTime);
    alert("Test schedule time saved! The Cron Job will now deliver this exactly at the specified time.");
  };

  const dayName = dayData.title.split(',')[0];
  const previousDay = new Date(cutoff).toLocaleDateString('en-US', { weekday: 'long' });

  const getTextLength = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent.trim().length;
  };
  const charCount = getTextLength(content);

  const isReadOnly = (status === 'scheduled' || isLocked) && !devMode;

  return (
    <Card id={`letter-card-${dayData.day}`} className="p-0 overflow-hidden relative shadow-sm border-tan">
      {status === 'scheduled' && !devMode && (
        <SafeIcon name="Lock" className="absolute top-4 right-4 text-5xl text-forest-green opacity-10 pointer-events-none" />
      )}
      
      {errorToast && (
        <div className="absolute top-0 left-0 w-full bg-red-600 text-white text-center text-sm py-1 font-bold z-20">
          {errorToast}
        </div>
      )}

      <div className="bg-[#EAF1E6] border-l-4 border-forest-green p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <h3 className="font-bold text-forest-green tracking-wide text-lg">DAY {dayData.day} — {dayData.title}</h3>
          <p className="text-sm text-muted mt-1">Arrives in your inbox {dayName} at 6:00am</p>
        </div>
        <div className="shrink-0 text-right">
          {status === 'not_written' && <span className="border-2 border-amber text-amber text-xs font-bold px-3 py-1 rounded-full uppercase">Not written</span>}
          {status === 'draft' && <span className="bg-amber text-white text-xs font-bold px-3 py-1 rounded-full uppercase">Draft saved</span>}
          {status === 'scheduled' && <span className="bg-forest-green text-white text-xs font-bold px-3 py-1 rounded-full uppercase">Scheduled ✓</span>}
          {status === 'delivered' && <span className="bg-gwa-blue text-white text-xs font-bold px-3 py-1 rounded-full uppercase">Delivered</span>}
        </div>
      </div>

      <div className="p-6 md:p-8">
        <div className="bg-tan/30 border-l-4 border-forest-green p-5 rounded-r-md mb-8">
          <p className="text-dark-green font-medium text-lg leading-relaxed">{dayData.prompt}</p>
        </div>

        {status === 'delivered' && !devMode ? (
          <div className="relative bg-cream p-8 rounded-md border border-tan mt-4 overflow-hidden">
            <SafeIcon name="Mail" className="absolute -right-8 -bottom-8 text-[150px] text-tan opacity-30 pointer-events-none" />
            <div 
              className="relative z-10 text-dark-green font-poppins italic text-[20px] leading-loose camper-letter-rendered"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
        ) : (
          <div>
            <div className={`rounded-md overflow-hidden transition-all ${
                isReadOnly
                  ? 'bg-cream border border-tan opacity-90 cursor-not-allowed' 
                  : 'bg-cream border border-amber focus-within:ring-2 focus-within:ring-amber/50'
              }`}>
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                readOnly={isReadOnly}
                modules={isReadOnly ? { toolbar: false } : quillModules}
                className="quill-camper-letter"
                placeholder={`Dear ${user?.name?.split(' ')[0] || 'Friend'},`}
              />
            </div>
            
            <div className="flex justify-between items-center mt-3 text-sm text-muted h-6">
              <div>
                {charCount} characters
                {charCount > 0 && charCount < 50 && (
                  <span className="text-amber ml-2 italic">Your future self will appreciate a few more words.</span>
                )}
              </div>
              <div className="text-amber font-medium transition-opacity duration-500 flex items-center gap-1">
                {autoSaveTime && <><SafeIcon name="Check" /> Draft saved {autoSaveTime}</>}
              </div>
            </div>

            {(status === 'not_written' || status === 'draft' || devMode) && !isLocked && (
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                {!confirming ? (
                  <>
                    <Button variant="outline" className="border-2 border-forest-green text-forest-green bg-white hover:bg-cream px-8 py-3 text-lg" onClick={handleManualSave}>
                      Save Draft
                    </Button>
                    <Button className="bg-forest-green text-white hover:bg-dark-green px-8 py-3 text-lg shadow-md" onClick={() => setConfirming(true)}>
                      Schedule This Letter
                    </Button>
                  </>
                ) : (
                  <div className="bg-cream border border-amber rounded-md p-6 w-full animate-fadeIn shadow-inner">
                    <p className="text-dark-green font-medium mb-5 text-base">
                      Once scheduled, this letter will be locked and delivered to <b>{user.email}</b> on <b>{dayName}</b> at 6am. 
                      You can still edit it until midnight on {previousDay}. Schedule it now?
                    </p>
                    <div className="flex gap-4">
                      <Button onClick={() => { onSchedule(dayData.day, content); setConfirming(false); }} className="bg-forest-green text-white px-8 py-2 text-lg">
                        Confirm
                      </Button>
                      <Button variant="ghost" onClick={() => setConfirming(false)} className="text-muted text-lg">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {status === 'scheduled' && !devMode && (
              <div className="mt-8">
                {!editingUnschedule ? (
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="bg-forest-green text-white px-5 py-3 rounded-md font-bold text-base flex items-center gap-2 shadow-sm">
                      <SafeIcon name="Check" className="text-lg" /> Scheduled — Delivering {dayName} at 6am
                    </div>
                    {!isLocked && (
                      <button onClick={() => setEditingUnschedule(true)} className="text-base text-forest-green font-medium hover:underline flex items-center gap-1 ml-2">
                        <SafeIcon name="Edit3" className="text-sm" /> Edit
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-md p-6 animate-fadeIn">
                    <p className="text-red-700 font-medium mb-4 text-base">
                      <SafeIcon name="AlertTriangle" className="inline mr-2 text-lg" /> 
                      Editing will unschedule this letter. You'll need to reschedule it before midnight {previousDay}.
                    </p>
                    <div className="flex gap-4">
                      <Button onClick={() => { onUnschedule(dayData.day); setEditingUnschedule(false); }} className="bg-red-600 text-white hover:bg-red-700 px-6 py-2">
                        Unschedule & Edit
                      </Button>
                      <Button variant="ghost" onClick={() => setEditingUnschedule(false)} className="text-muted px-4 py-2">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isLocked && status !== 'delivered' && status !== 'scheduled' && !devMode && (
              <div className="mt-6 text-base text-amber font-bold flex items-center gap-2 bg-amber/10 p-4 rounded-md border border-amber/20">
                <SafeIcon name="Lock" className="text-lg" /> Time has expired to schedule this letter.
              </div>
            )}

            {devMode && (
              <div className="mt-8 p-5 bg-gray-100 border border-gray-300 rounded-md border-dashed animate-fadeIn">
                <h4 className="font-bold text-gray-700 text-sm mb-4 flex items-center gap-2">
                  <SafeIcon name="Tool" /> Admin Test Controls
                </h4>
                
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-2 items-center">
                    <input 
                      type="email" 
                      className="border border-gray-300 rounded px-3 py-2 text-sm w-full sm:w-64 bg-white" 
                      value={testEmail} 
                      onChange={e => setTestEmail(e.target.value)} 
                      placeholder="Recipient test email"
                    />
                    <Button onClick={handleTestEmail} disabled={sendingTest} className="w-full sm:w-auto bg-gray-800 hover:bg-black text-white shrink-0">
                      {sendingTest ? 'Sending...' : 'Send Test Email'}
                    </Button>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-center gap-2 w-full">
                    <input 
                      type="datetime-local" 
                      className="border border-gray-300 rounded px-3 py-2 text-sm w-full sm:w-64 bg-white" 
                      value={customTime} 
                      onChange={e => setCustomTime(e.target.value)} 
                    />
                    <Button onClick={handleCustomSchedule} className="bg-gwa-blue hover:bg-blue-800 text-white w-full sm:w-auto whitespace-nowrap">
                      Test Schedule Time
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default function Letters() {
  const { user, loading: authLoading } = useAuth();
  const { currentRun, currentEnrollment, loadingAcademy } = useAcademy();
  const [letters, setLetters] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');
  const [devMode, setDevMode] = useState(false);
  const [lettersOpen, setLettersOpen] = useState(true);

  const safeUser = Array.isArray(user) ? user[0] : user;
  const effectivePassType = currentEnrollment?.pass_type || safeUser?.pass_type;
  const isAAPUser = safeUser?.role === 'admin' || ['allaccess', 'evergreen-aap'].includes(effectivePassType);

  // Compute deadline from run's start_date: lock at midnight on start_date
  let runStartObj = null;
  if (currentRun?.start_date) {
    const cleanDate = String(currentRun.start_date).trim().split('T')[0];
    const parts = cleanDate.split('-').map(Number);
    if (parts.length === 3 && !parts.some(isNaN)) {
      runStartObj = new Date(parts[0], parts[1] - 1, parts[2]);
    } else {
      const d = new Date(currentRun.start_date);
      if (!isNaN(d.getTime())) runStartObj = d;
    }
  }

  const cutoffDateObj = runStartObj ? new Date(runStartObj) : new Date('2026-07-13T00:00:00');
  cutoffDateObj.setHours(0, 0, 0, 0);
  const d1Cutoff = cutoffDateObj.getTime();

  const prevDayNoticeObj = new Date(cutoffDateObj);
  prevDayNoticeObj.setDate(prevDayNoticeObj.getDate() - 1);
  const prevDayNotice = prevDayNoticeObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = d1Cutoff - now;

      if (distance < 0) {
        setTimeLeft('Your letters are written and scheduled. See you at the academy.');
        clearInterval(timer);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60));
      const minutes = Math.floor((distance % (1000 * 60)) / (1000 * 60));

      setTimeLeft(
        window.innerWidth < 768 && days === 0 
          ? `Locks in ${hours}h ${minutes}m`
          : `Letters lock in ${days}d ${hours}h ${minutes}m`
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [d1Cutoff]);

  const loadData = async () => {
    if (!safeUser?.id || !currentRun?.id) {
      return;
    }

    setLoading(true);
    try {
      const [resLetters, resSettings] = await Promise.all([
        api.getUnscoped('self_letters_gwaacademy', { limit: 1000 }),
        api.get('settings_gwaacademy')
      ]);
      
      const data = Array.isArray(resLetters) ? resLetters : resLetters.data || [];
      const currentUserIdStr = String(safeUser?.id);
      const currentUserOwnerIdStr = String(safeUser?.owner_id || safeUser?.id);
      const currentRunIdStr = String(currentRun.id);

      const userRunLetters = data.filter((l) => {
        if (!l || !l.day) return false;
        
        const isUserMatch = 
          String(l.user_id) === currentUserIdStr || 
          String(l.owner_id) === currentUserIdStr ||
          String(l.user_id) === currentUserOwnerIdStr ||
          String(l.owner_id) === currentUserOwnerIdStr;

        if (!isUserMatch) return false;

        if (l.run_id) {
          return String(l.run_id) === currentRunIdStr;
        }
        if (l.session_id && currentRun.session_id) {
          return String(l.session_id) === String(currentRun.session_id);
        }
        return true;
      });

      userRunLetters.sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));

      const map = {};
      userRunLetters.forEach(l => { 
        const dayNum = Number(l.day);
        if (dayNum >= 1 && dayNum <= 5) {
          if (!map[dayNum]) {
            map[dayNum] = l;
          } else {
            const prevBody = (map[dayNum].letter_body || '').trim();
            const newBody = (l.letter_body || '').trim();
            if (newBody.length > prevBody.length || l.status === 'scheduled' || l.status === 'delivered') {
              map[dayNum] = l;
            } else if (newBody.length > 0 && Number(l.id) > Number(map[dayNum].id)) {
              map[dayNum] = l;
            }
          }
        }
      });
      setLetters(map);

      const dataSettings = Array.isArray(resSettings) ? resSettings : resSettings.data || [];
      const letterSetting = dataSettings.find(s => s.setting_key === 'letters_module_open' || s.key === 'letters_module_open');
      if (letterSetting && letterSetting.value === 'false') {
        setLettersOpen(false);
      } else {
        setLettersOpen(true);
      }
    } catch (e) {
      console.error('Error loading data', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading || loadingAcademy) return;
    if (!isAAPUser) {
      setLoading(false);
      return;
    }

    loadData();
  }, [authLoading, loadingAcademy, isAAPUser, safeUser?.id, currentRun?.id]);

  if (authLoading || loadingAcademy || loading) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center min-h-[500px]">
        <div className="text-center text-muted flex flex-col items-center gap-4">
          <SafeIcon name="Mail" className="text-5xl text-amber animate-pulse" />
          <p className="text-lg font-medium">Unlocking your studio...</p>
        </div>
      </div>
    );
  }

  if (!isAAPUser) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4">
        <Card className="bg-cream text-center py-20 px-8 border-tan shadow-sm">
          <div className="w-24 h-24 bg-forest-green/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <SafeIcon name="Lock" className="text-5xl text-forest-green" />
          </div>
          <h2 className="text-3xl font-bold text-dark-green mb-6 font-poppins">This is an All-Access Feature</h2>
          <p className="text-lg text-muted max-w-2xl mx-auto mb-10 leading-relaxed">
            All-Access pass holders write themselves a letter for each day of the academy — delivered to their inbox every morning at 6am.
          </p>
          <Link to="/upgrade" className="inline-block bg-amber text-white px-10 py-4 rounded-md font-bold text-lg hover:bg-orange-600 transition-colors shadow-md">
            Upgrade to All-Access
          </Link>
        </Card>
      </div>
    );
  }

  if (!lettersOpen && safeUser?.role !== 'admin' && safeUser?.early_access != 1) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4">
        <Card className="bg-cream text-center py-20 px-8 border-tan shadow-sm">
          <div className="w-24 h-24 bg-forest-green/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <SafeIcon name="Lock" className="text-5xl text-forest-green" />
          </div>
          <h2 className="text-3xl font-bold text-dark-green mb-6 font-poppins">Letters Module is Locked</h2>
          <p className="text-lg text-muted max-w-2xl mx-auto mb-10 leading-relaxed">
            The self-letter writing module is currently closed. It will open closer to the start of the academy!
          </p>
        </Card>
      </div>
    );
  }

  const handleSave = async (day, content, isAutoSave = false) => {
    if (!safeUser?.id || !currentRun?.id) return false;

    const currentData = letters[day] || { day, status: 'draft' };
    const payload = { 
      day: Number(day), 
      letter_body: content, 
      status: 'draft', 
      user_id: String(safeUser?.id),
      owner_id: String(safeUser?.owner_id || safeUser?.id),
      department_id: String(currentRun?.department_id || currentRun?.session_id || 1),
      session_id: String(currentRun?.session_id || 1),
      run_id: String(currentRun?.id),
      scheduled_send_at: null
    };
    
    try {
      if (currentData.id) {
        await api.putUnscoped('self_letters_gwaacademy', currentData.id, payload);
        setLetters(prev => ({ 
          ...prev, 
          [day]: { 
            ...currentData, 
            ...payload,
            id: currentData.id
          } 
        }));
      } else {
        const res = await api.postUnscoped('self_letters_gwaacademy', payload);
        const createdId = res?.data?.id || res?.id || (Array.isArray(res?.data) && res.data[0]?.id) || (Array.isArray(res) && res[0]?.id);
        const newRecord = {
          ...payload,
          id: createdId || Date.now()
        };
        setLetters(prev => ({ 
          ...prev, 
          [day]: newRecord
        }));
      }
      return true;
    } catch (e) {
      console.error('Save failed', e);
      return false;
    }
  };

  const handleSchedule = async (day, content, customScheduledTime = null) => {
    if (!safeUser?.id || !currentRun?.id) return;

    const currentData = letters[day] || { day };
    const dayData = PROMPTS.find(p => p.day === day);

    const toMysqlDatetime = (isoString) => {
      return isoString.replace('T', ' ').replace(/\.\d{3}Z$/, '');
    };

    const convertNyLocalToUtcIso = (localDateTimeStr) => {
      let normalized = localDateTimeStr.replace(' ', 'T');
      if (normalized.length > 16) normalized = normalized.slice(0, 16);
      if (normalized.length === 10) normalized += 'T00:00';

      const fakeUtcDate = new Date(`${normalized}Z`);

      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      }).formatToParts(fakeUtcDate);

      const p = {};
      parts.forEach(part => { p[part.type] = part.value; });
      let hour = p.hour;
      if (hour === '24') hour = '00';

      const nyAsUtc = new Date(`${p.year}-${p.month}-${p.day}T${hour}:${p.minute}:${p.second}Z`);
      const offsetMs = fakeUtcDate.getTime() - nyAsUtc.getTime();
      const trueUtcDate = new Date(fakeUtcDate.getTime() + offsetMs);

      return trueUtcDate.toISOString();
    };

    let localSendTime = `${dayData.date}T06:00`;
    let utcSendTime = convertNyLocalToUtcIso(localSendTime);

    if (customScheduledTime) {
      utcSendTime = convertNyLocalToUtcIso(customScheduledTime);
    }

    const payload = { 
      day: Number(day), 
      letter_body: content, 
      status: 'scheduled', 
      user_id: String(safeUser?.id),
      owner_id: String(safeUser?.owner_id || safeUser?.id),
      department_id: String(currentRun?.department_id || currentRun?.session_id || 1),
      session_id: String(currentRun?.session_id || 1),
      run_id: String(currentRun?.id),
      scheduled_send_at: toMysqlDatetime(utcSendTime)
    };

    if (customScheduledTime) {
      payload.test_scheduled_time = toMysqlDatetime(utcSendTime);
    }
    
    try {
      if (currentData.id) {
        await api.putUnscoped('self_letters_gwaacademy', currentData.id, payload);
        setLetters(prev => ({ 
          ...prev, 
          [day]: { 
            ...currentData, 
            ...payload,
            id: currentData.id
          } 
        }));
      } else {
        const res = await api.postUnscoped('self_letters_gwaacademy', payload);
        const createdId = res?.data?.id || res?.id || (Array.isArray(res?.data) && res.data[0]?.id) || (Array.isArray(res) && res[0]?.id);
        setLetters(prev => ({ 
          ...prev, 
          [day]: { 
            ...payload,
            id: createdId || Date.now()
          } 
        }));
      }
    } catch (e) {
      console.error('Schedule failed', e);
      alert('Failed to schedule letter. Please try again.');
    }
  };

  const handleUnschedule = async (day) => {
    const currentData = letters[day];
    if (!currentData || !currentData.id) return;
    
    try {
      await api.putUnscoped('self_letters_gwaacademy', currentData.id, { 
        status: 'draft',
        scheduled_send_at: null
      });
      setLetters(prev => ({ 
        ...prev, 
        [day]: { 
          ...currentData, 
          status: 'draft',
          scheduled_send_at: null 
        } 
      }));
    } catch (e) {
      console.error('Unschedule failed', e);
    }
  };

  const writtenCount = Object.values(letters).filter(l => l.status === 'scheduled' || l.status === 'delivered').length;

  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-24 relative">
      <style>{`
        .quill-camper-letter .ql-toolbar {
          background: #ffffff;
          border: none;
          border-bottom: 1px solid #e5e7eb;
          border-radius: 6px 6px 0 0;
          padding: 12px 16px;
        }
        .quill-camper-letter .ql-container {
          border: none;
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          line-height: 1.8;
          color: #2c3e20;
          min-height: 250px;
        }
        .quill-camper-letter .ql-editor {
          padding: 24px;
        }
        .quill-camper-letter .ql-editor p {
          margin-bottom: 1.25em;
        }
        .quill-camper-letter .ql-editor ul {
          margin-bottom: 1.25em;
        }
        .quill-camper-letter .ql-editor li {
          margin-bottom: 0.5em;
        }
        
        .camper-letter-rendered p {
          margin-bottom: 1.25em;
        }
        .camper-letter-rendered ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin-bottom: 1.25em;
        }
        .camper-letter-rendered li {
          margin-bottom: 0.5em;
        }
      `}</style>
      
      {/* Notice for student users at top */}
      <div className="bg-amber/15 border border-amber rounded-md p-4 flex items-start gap-3">
        <SafeIcon name="Clock" className="text-amber text-xl shrink-0 mt-0.5" />
        <div className="text-sm text-dark-green">
          <span className="font-bold">Notice: </span>
          Please complete and schedule your self-letters before the end of the day before the academy starts (midnight on {prevDayNotice}). The Self-Letter Studio locks automatically at midnight on the session start date.
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 bg-white p-6 md:p-8 rounded-lg border border-tan shadow-sm">
        <div className="flex-1">
          <h1 className="text-3xl md:text-4xl font-bold text-forest-green font-poppins flex items-center gap-4">
            <SafeIcon name="Mail" className="text-4xl" />
            Your Academy Letters
          </h1>
          <p className="text-muted mt-4 max-w-2xl text-lg leading-relaxed">
            Before the academy begins, write yourself a letter for each morning. They'll arrive in your inbox at 6am.
          </p>
        </div>
        
        <div className="w-full md:w-auto md:text-right shrink-0 border-t md:border-t-0 border-tan pt-6 md:pt-0">
          <div className="text-base font-bold text-dark-green mb-3">{writtenCount} of 5 letters written</div>
          <div className="hidden md:flex gap-3 justify-end">
            {[1, 2, 3, 4, 5].map(d => {
              const stat = letters[d]?.status;
              let color = "text-gray-300";
              if (stat === 'draft') color = "text-amber opacity-60";
              if (stat === 'scheduled' || stat === 'delivered') color = "text-forest-green";
              return <SafeIcon key={d} name="Mail" className={`text-3xl ${color}`} />;
            })}
          </div>
          <div className="text-sm font-medium text-amber mt-4 bg-amber/10 px-4 py-2 rounded-full inline-block w-full md:w-auto text-center">
            <SafeIcon name="Clock" className="inline mr-2 text-base" />
            {timeLeft}
          </div>
        </div>
      </div>

      {safeUser?.role === 'admin' && (
        <div className="text-right">
          <button 
            onClick={() => setDevMode(!devMode)}
            className={`text-xs px-3 py-1.5 rounded-md border font-medium transition-colors ${
              devMode 
                ? 'bg-gray-800 text-white border-gray-800' 
                : 'text-gray-500 hover:text-amber border-dashed border-gray-300 hover:border-amber'
            }`}
          >
            {devMode ? 'Disable Dev Testing' : 'Enable Admin Test Mode'}
          </button>
        </div>
      )}

      <div className="space-y-10">
        {PROMPTS.map(dayData => (
          <LetterCard 
            key={dayData.day} 
            dayData={dayData} 
            letter={letters[dayData.day]} 
            user={safeUser}
            onSave={handleSave}
            onSchedule={handleSchedule}
            onUnschedule={handleUnschedule}
            devMode={devMode}
            globalCutoff={cutoffDateObj}
          />
        ))}
      </div>
    </div>
  );
}